import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatRelative } from "../lib/api";
import { Avatar, AvatarFallback } from "./ui/avatar";
import AuthImage from "./AuthImage";
import { Star, Filter } from "lucide-react";
import UserBadges from "./UserBadges";

/**
 * Privat favoritt-liste. Vises kun for kontoeier i Profile.jsx.
 * Andre brukere får INGEN beskjed om at de er favoritt-markert.
 */
export default function MyFavoritesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlineOnly, setOnlineOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/favorites", {
        params: { online_only: onlineOnly },
      });
      setItems(data.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlineOnly]);

  const remove = async (username) => {
    try {
      await api.post(`/users/${username}/favorite`);
      setItems((prev) => prev.filter((i) => i.username !== username));
    } catch (e) {}
  };

  if (loading) {
    return <div className="font-fraunces italic text-[var(--cream-soft)]">Laster favoritter…</div>;
  }

  return (
    <div className="space-y-6" data-testid="favorites-list">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="font-fraunces italic text-[var(--cream-soft)] text-sm">
          Din private liste. Brukerne får ingen beskjed om at du har lagret dem.
        </p>
        <button
          onClick={() => setOnlineOnly((v) => !v)}
          className={`btn-ghost text-[10px] ${onlineOnly ? "text-[var(--magenta)]" : ""}`}
          data-testid="favorites-online-filter"
        >
          <Filter className="h-3 w-3 inline mr-2" />
          {onlineOnly ? "Vis alle" : "Kun online nå"}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="editorial-card p-8 text-center">
          <Star className="h-8 w-8 mx-auto mb-3 text-[var(--cream-faint)]" />
          <div className="font-fraunces italic text-[var(--cream-soft)] mb-2">
            {onlineOnly
              ? "Ingen av favorittene dine er online akkurat nå."
              : "Du har ikke lagt til noen favoritter ennå."}
          </div>
          <p className="font-manrope text-xs text-[var(--cream-faint)]">
            Trykk stjerne-ikonet på en bruker for å legge dem til her.
          </p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((u) => (
            <li
              key={u.id}
              className="editorial-card p-4 flex items-center gap-3"
              data-testid={`favorite-item-${u.username}`}
            >
              <Link to={`/profil/${u.username}`} className="shrink-0 relative">
                <Avatar className="h-12 w-12">
                  {u.avatar_url ? (
                    <AuthImage src={u.avatar_url} alt={u.username} className="h-12 w-12 object-cover rounded-full" />
                  ) : (
                    <AvatarFallback className="bg-[var(--magenta)]/20 text-[var(--magenta)] uppercase">
                      {u.username?.slice(0, 2)}
                    </AvatarFallback>
                  )}
                </Avatar>
                <span
                  className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-[var(--noir)] ${
                    u.online_status === "online"
                      ? "bg-emerald-500"
                      : u.online_status === "recent"
                      ? "bg-amber-500"
                      : "bg-zinc-600"
                  }`}
                  title={
                    u.online_status === "online"
                      ? "Online nå"
                      : u.online_status === "recent"
                      ? "Aktiv nylig"
                      : "Offline"
                  }
                  data-testid={`favorite-status-${u.username}`}
                />
              </Link>
              <div className="min-w-0 flex-1">
                <Link
                  to={`/profil/${u.username}`}
                  className="font-fraunces text-[var(--cream)] hover:text-[var(--magenta)] transition-colors truncate inline-flex items-center gap-1"
                >
                  <span className="truncate">{u.username}</span>
                  <UserBadges user={u} size="sm" />
                </Link>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] uppercase tracking-[0.15em] font-manrope text-[var(--cream-faint)]">
                  <span>
                    {u.online_status === "online"
                      ? "online"
                      : u.last_seen
                      ? `sist ${formatRelative(u.last_seen)}`
                      : "offline"}
                  </span>
                </div>
              </div>
              <button
                onClick={() => remove(u.username)}
                className="text-[var(--magenta)] hover:text-[var(--magenta)]/70 transition-colors"
                title="Fjern fra favoritter"
                data-testid={`favorite-remove-${u.username}`}
              >
                <Star className="h-4 w-4 fill-current" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
